/** Changes since the last brief (ticket body, plan §8). Only the "added"
 * entries are actionable — "resolved" entries are history, not something to
 * act on — and an added entry whose key is in `overview.suppressedKeys`
 * (the server's `isHelmItemSuppressed` verdict — acted on elsewhere, e.g.
 * the same ticket marked Seen in Shipped) stays hidden. Every row names the
 * item, not only the generic "newly critical"/"no longer critical" reason.
 * A row's name is, in order (A7Y-5873): the `name` the run stored with the
 * change — the row as Today showed it; the row as it stood in the facts that
 * held it (`helmCriticalRowName` over the current facts for an added row, the
 * previous run's for a resolved one, which HelmPage fetches only when an entry
 * lacks a name); `buildHelmDisplayNameIndex`; and last `fallbackChangeName`,
 * which says a title is unavailable rather than print a key's slug or a bare
 * internal id. */
import { Table, TableBody, TableCell, TableRow } from '@a7y/tailwind-catalyst';
import { helmCriticalRowName } from '@a7y/platform-health/helm';
import type { HelmActionInput, HelmFacts } from '@a7y/platform-health/helm';
import { buildHelmDecisionIndex, buildHelmDisplayNameIndex, fallbackChangeName } from '../links';
import { ItemActionsMenu } from '../ItemActionsMenu';
import { HelmEmpty, HelmSection } from './HelmSection';

export function Changes({
  facts,
  previousFacts = null,
  suppressedKeys,
  onAction,
}: {
  facts: HelmFacts;
  /** The previous run's facts (A7Y-5873): where a resolved row stood, for a run that stored no name. */
  previousFacts?: HelmFacts | null;
  suppressedKeys: ReadonlySet<string>;
  onAction: (input: HelmActionInput) => void;
}) {
  const decisionIndex = buildHelmDecisionIndex(facts);
  const nameIndex = buildHelmDisplayNameIndex(facts);
  // A7Y-5873: the name the run stored — the row as Today showed it; else the row as it
  // stood in the facts that held it (the previous run's for a resolved row); else a
  // name that says it is not known, never the key's slug.
  const nameFor = (change: { key: string; name?: string }, where: HelmFacts | null) =>
    change.name ??
    helmCriticalRowName(where, change.key) ??
    nameIndex.get(change.key) ??
    fallbackChangeName(change.key);
  const added = facts.changes_since_last.added.filter((c) => !suppressedKeys.has(c.key));
  const resolved = facts.changes_since_last.resolved;
  const empty = added.length === 0 && resolved.length === 0;

  return (
    <HelmSection title="Changes since last brief" lineOn="none">
      {empty ? (
        <HelmEmpty>none</HelmEmpty>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
              Added
            </span>
            {added.length === 0 ? (
              <HelmEmpty>none</HelmEmpty>
            ) : (
              <Table dense className="[--gutter:--spacing(0)]">
                <TableBody>
                  {added.map((c) => (
                    <TableRow key={c.key}>
                      <TableCell className="min-w-0 text-sm break-words whitespace-normal">
                        <span className="font-medium">{nameFor(c, facts)}</span> — {c.label}
                      </TableCell>
                      <TableCell>
                        <ItemActionsMenu
                          itemKey={c.key}
                          decision={decisionIndex.get(c.key) ?? null}
                          onAction={onAction}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div>
            <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
              Resolved
            </span>
            {resolved.length === 0 ? (
              <HelmEmpty>none</HelmEmpty>
            ) : (
              <Table dense className="[--gutter:--spacing(0)]">
                <TableBody>
                  {resolved.map((c) => (
                    <TableRow key={c.key}>
                      <TableCell className="min-w-0 text-sm break-words whitespace-normal">
                        <span className="font-medium">{nameFor(c, previousFacts)}</span> — {c.label}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      )}
    </HelmSection>
  );
}
